import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActionPerformed, PushNotifications, PushNotificationSchema, PermissionStatus} from '@capacitor/push-notifications';
import { Platform, ToastController } from '@ionic/angular';
import { getLogger } from "../../shared/logging"
import { TextableService } from './textable-service.service';
import { UserManagementService } from './user-management.service';
import { Router } from '@angular/router';
import { AuthNZService } from '../core/authnz.service';
import { FCM } from '@capacitor-community/fcm';
import { ApplicationStates } from '../core/auth-nz/interfaces';
import { BackendService } from './backend.service';

const log = getLogger("ControlMessagingService");
log.enableAll();

const REGISTRATION_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root'
})
export class ControlMessagingService {

  private currentToken: string;
  private currentUID: string;

  private registrationTimeout: ReturnType<typeof window.setTimeout>;

  public notificationStatus: string = "Not registered";

  constructor(
    private userService: UserManagementService,
    private afa: AngularFireAuth,
    public toast: ToastController,
    public router: Router,
    public authnz: AuthNZService,
    private platform: Platform,
    private backendService: BackendService
  ) {
    authnz.authState$.subscribe(async (state)=>{
      if (state == ApplicationStates.LoggedIn) {
        this.currentUID = (await this.afa.currentUser).uid;
        this.SetupNotifications();
      }
    });
  }

  public async Troubleshoot() {
    this.SetupNotifications();
  }

  private async SetupNotifications() {
    let platformsString = JSON.stringify(this.platform.platforms());
    if (!this.platform.is("hybrid")){
      log.warn(`Not setting up notifications for user '${this.currentUID}'; Current platform(s) ` + platformsString )
      this.notificationStatus = "Disabled on this platform";
      return;
    }

    log.debug(`Setting up push notifications for user '${this.currentUID}'; Current platform(s) ` + platformsString);
    this.notificationStatus = "Initializing";
    let result: PermissionStatus;
    try {
      const resultp = PushNotifications.requestPermissions()
      this.notificationStatus = "Waiting for permission";
      result = await resultp;
    }
    catch (err) {
      log.warn("Error getting push notification permission")
      this.notificationStatus = "Failed requesting permission";
      return;
    }

    if (result.receive !== 'granted') {
      log.debug("Notification Permission not granted")
      this.notificationStatus = "Permission denied";
      return;
    }

    log.debug("Notification Permission " + result.receive);
    this.notificationStatus = "Registering listeners";
    await this.RegisterListeners();
    this.notificationStatus = "Registered listeners";
    this.registrationTimeout = setTimeout(()=>{
      //TODO: TXBDEV-1364 - Capture this in some kind of telemetry 
      //TODO: TXBDEV-2339 - Tell the user if we think notifications might be broken
      log.warn(`Registration token not received after ${REGISTRATION_TIMEOUT_MS} ms; notifications may not work`);
      this.notificationStatus = "Registration timed out";
    }, REGISTRATION_TIMEOUT_MS);
    await PushNotifications.register();
    await this.HandleNewToken();
  }

  private async HandleNewToken() {
    this.notificationStatus = "Waiting for token";
    if (this.platform.is("android")) {
      await new Promise<void>((resolve, reject)=>{
        PushNotifications.addListener('registration', async(token)=>{
          this.currentToken = token.value;
          resolve();
        });
      });
    }
    else {
      this.currentToken = (await FCM.getToken()).token
    }
    clearTimeout(this.registrationTimeout);
    log.debug("Received new PushNotification token")
    await this.userService.addUserDeviceToken(this.currentUID, this.currentToken);
    this.authnz.preLogoutTasks.push(async ()=>{
      await this.RemoveNotifications();
    })
    this.notificationStatus = "Registered";
  }

  /**
   * Register callback handlers for all of the events from PushNotification
   */
  private async RegisterListeners() {
   

    PushNotifications.addListener('registrationError', (err) => {
      log.error("Could not register PushNotifications", err);
      this.notificationStatus = "Failed to Register";
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived',(notification: PushNotificationSchema) => {
      this.HandlePushNotification(notification)
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      this.HandlePushNotificationAction(action)
    });
  }

  private async HandlePushNotification (notification: PushNotificationSchema) {
    log.debug("Received notification", notification);
    const toast = await this.toast.create({
      message: 'New message from '+(notification.data.full_name || notification.data.phone_number),
      duration: 2000,
      position: 'bottom',
      buttons: [
        {
          side: 'end',
          text: 'View',
          handler: async () => {
            this.notificationNavigate(notification);
          }
        }, {
          text: 'Done',
          role: 'cancel',
          handler: () => {
            toast.dismiss();
          }
        }
      ]
    });
    toast.present();
  }
  
  private async HandlePushNotificationAction (action: ActionPerformed) {
    log.debug("Handling action", action)
    this.notificationNavigate(action.notification);
  }

  /**
   * Navigates to the conversation indicated by the notification
   * if data is missing (e.g. test notifications), then just
   * navigates to `/tabs/conversations`
   * 
   * @param notification 
   */
  private async notificationNavigate(notification: PushNotificationSchema) {
    await this.authnz.setActiveNumber(notification.data.activeUserID);
    let navigateTarget:string[] = [];
    if (notification.data.id) {
      navigateTarget= ['/conversations', notification.data.id];
    }
    else {
      navigateTarget= ['/tabs/conversations'] // TODO: TXBDEV-2338 - Refactor the routes to make sense.
    }
    log.debug("Navigating to ", navigateTarget);
    this.router.navigate(navigateTarget)
  }

  public async RemoveNotifications() { 
    if (!this.platform.is("hybrid")){
      log.warn("Not cleaning up notifications on unsupported platform")
    }
    log.debug("Cleaning up push notifications");
    await this.userService.removeUserDeviceToken(this.currentUID, this.currentToken);
    await PushNotifications.removeAllListeners();
  
  }

  public async testNotification() {
    await this.backendService.backendPost(`api/v2/users/${this.currentUID}/testNotifitactions`,{},{});
  }
}
